import bankOperationsSaleLeftPanel from "@/store/modules/finances/bankOperationsSale/bankOperationsSaleLeftPanel";
import bankOperationsPurchaseLeftPanel from "@/store/modules/finances/bankOperationsPurchase/bankOperationsPurchaseLeftPanel";
import bankOperationsPurchaseRightPanel from "@/store/modules/finances/bankOperationsPurchase/bankOperationsPurchaseRightPanel";

/*eslint  @typescript-eslint/no-use-before-define: 0*/

import { defineModule } from "direct-vuex";
import { moduleActionContext, moduleGetterContext } from "@/store";
import bankOperationsSaleRightPanel from "@/store/modules/finances/bankOperationsSale/bankOperationsSaleRightPanel";
import { BankStatementMatchDocument, BankStatementMatchReservation } from "@/models/api/finances/BankStatementMatch";
import {
  addAutomaticallyMatchDocument,
  addManualMatchDocument,
  getBankStatementItemMatchDataDocument,
  getManualBankStatementItemMatchDataDocument,
  removeBankStatementItemMatchDocument,
  getBankStatementItemMatchDataReservation,
  getManualBankStatementItemMatchDataReservation,
  removeBankStatementItemMatchReservation,
  addAutomaticallyMatchReservation,
  addManualMatchReservation,
  getDocumentPaymentsForBankStatementItem,
  getReservationPaymentsForBankStatementItem,
} from "@/requests/finances/requestsBankOperations";
import { ConfirmModel } from "@/models/components/popup/Confirm";
import { filterCapitalize } from "@/filters/capitalize";
import i18n from "@/plugins/i18n";
import { PaymentForBankStatement } from "@/models/api/finances/PaymentForBankStatement";
import { getIsLockedBankStatementMatch } from "@/requests/finances/requestsBankTransfers";
import { MESSAGE_TYPES } from "@/static/messageTypes";
import { AlertModelInterface } from "@/models/components/popup/Alert";

export enum PAIRING_TYPE {
  MANUAL,
  AUTOMATIC,
}

/* eslint-disable-next-line */
export interface BankOperationsInterface {
  pairingType: null | PAIRING_TYPE;
  bankStatementMatchDocument: null | BankStatementMatchDocument;
  bankStatementMatchReservation: null | BankStatementMatchReservation;
  isDownloadTableMatched: boolean;
  isLoading: boolean;
  payments: Array<PaymentForBankStatement>;
  documentId: null | string;
  bookingItemId: null | number;
  paymentDialog: boolean;
  documentNumbers: string | null;
}

export const BankOperationsState: BankOperationsInterface = {
  pairingType: null,
  bankStatementMatchDocument: null,
  bankStatementMatchReservation: null,
  isDownloadTableMatched: false,
  isLoading: false,
  payments: [],
  documentId: null,
  bookingItemId: null,
  paymentDialog: false,
  documentNumbers: null,
};

const BankOperationsModule = defineModule({
  state: BankOperationsState as BankOperationsInterface,
  namespaced: true as const,
  modules: {
    saleLeftPanel: bankOperationsSaleLeftPanel,
    saleRightPanel: bankOperationsSaleRightPanel,
    purchaseLeftPanel: bankOperationsPurchaseLeftPanel,
    purchaseRightPanel: bankOperationsPurchaseRightPanel,
  },
  getters: {},
  mutations: {
    setPairingType(state, param: null | PAIRING_TYPE) {
      state.pairingType = param;
    },
    setBankStatementMatchDocument(state, param: null | BankStatementMatchDocument) {
      state.bankStatementMatchDocument = param;
    },
    setBankStatementMatchReservation(state, param: null | BankStatementMatchReservation) {
      state.bankStatementMatchReservation = param;
    },
    setIsDownloadTableMatched(state, param: boolean) {
      state.isDownloadTableMatched = param;
    },
    setDocumentNumbers(state, param: string | null) {
      state.documentNumbers = param;
    },
    setIsLoading(state, param: boolean) {
      state.isLoading = param;
    },
    setPayments(state, param: Array<PaymentForBankStatement>) {
      state.payments = param;
    },
    setDocumentId(state, param: null | string) {
      state.documentId = param;
    },
    setBookingItemId(state, param: null | number) {
      state.bookingItemId = param;
    },
    setPaymentDialog(state, param: boolean) {
      state.paymentDialog = param;
    },
  },
  actions: {
    async openManualPairing(context, documentId: string) {
      const { rootState, commit } = _moduleActionContext(context);
      commit.setBankStatementMatchDocument(null);
      const bankStatementItemId = rootState.finances.bankOperations.saleLeftPanel.selectedItemId as number;
      const response = await getBankStatementItemMatchDataDocument(bankStatementItemId, documentId);
      commit.setBankStatementMatchDocument(response);
      commit.setPairingType(PAIRING_TYPE.MANUAL);
    },
    async updateManualPairing(context, amount: number) {
      const { rootState, commit, state } = _moduleActionContext(context);
      const bankStatementItemId = rootState.finances.bankOperations.saleLeftPanel.selectedItemId as number;
      if (state.bankStatementMatchDocument !== null) {
        const response = await getManualBankStatementItemMatchDataDocument(
          bankStatementItemId,
          state.bankStatementMatchDocument.documentId,
          amount
        );
        commit.setBankStatementMatchDocument(response);
      }
    },
    async openAutomaticPairing(context, documentId: string) {
      const { rootState, commit } = _moduleActionContext(context);
      commit.setBankStatementMatchDocument(null);
      const bankStatementItemId = rootState.finances.bankOperations.saleLeftPanel.selectedItemId as number;
      const response = await getBankStatementItemMatchDataDocument(bankStatementItemId, documentId);
      commit.setBankStatementMatchDocument(response);
      commit.setPairingType(PAIRING_TYPE.AUTOMATIC);
    },
    async removePairing(context, documentId: string) {
      const { rootDispatch, rootState, dispatch, commit } = _moduleActionContext(context);
      const bankStatementItemId = rootState.finances.bankOperations.saleLeftPanel.selectedItemId as number;
      const isRemoveConfirmed = await dispatch.canUserRemoveBankStatementItemMatchDocument({
        bankStatementItemId,
        documentId,
      });
      if (!isRemoveConfirmed) return;

      const confirm = await rootDispatch.popup.addConfirm(
        new ConfirmModel(
          filterCapitalize(i18n.t("doYouReallyWantToRemovePairingWithAssignedPayment?")),
          filterCapitalize(i18n.t("warning")),
          [
            {
              text: filterCapitalize(i18n.t("cancel")),
              color: "warning",
              value: false,
              outlined: true,
            },
            {
              text: filterCapitalize(i18n.t("confirm")),
              color: "warning",
              value: true,
            },
          ],
          "450px"
        )
      );
      if (confirm) {
        const isDeleted = await removeBankStatementItemMatchDocument(bankStatementItemId, documentId);
        if (isDeleted) {
          rootDispatch.popup.addOperationSuccessfulSnackbar();
          await dispatch.saleLeftPanel.getDataTableData();
          commit.saleLeftPanel.setSelectedItemId(bankStatementItemId);
          // noinspection ES6MissingAwait
          dispatch.saleRightPanel.getDataTableData(bankStatementItemId);
        } else {
          const openDialog = await rootDispatch.popup.addConfirm(
            new ConfirmModel(
              filterCapitalize(i18n.t("xTurBankStatementToManyMatchesException")),
              filterCapitalize(i18n.t("warning")),
              [
                {
                  text: filterCapitalize(i18n.t("yes")),
                  color: "warning",
                  value: true,
                  outlined: true,
                },
                {
                  text: filterCapitalize(i18n.t("no")),
                  color: "warning",
                  value: false,
                },
              ],
              "450px"
            )
          );
          if (openDialog) {
            commit.setDocumentId(documentId);
            dispatch.downloadDocumentPayments();
            commit.setPaymentDialog(true);
          }
        }
      }
    },
    async canUserRemoveBankStatementItemMatchDocument(
      context,
      params: { bankStatementItemId: number; documentId: string }
    ) {
      const { rootDispatch } = _moduleActionContext(context);
      const isLockedEditResponse = await getIsLockedBankStatementMatch(params.bankStatementItemId, params.documentId);
      if (!isLockedEditResponse.isLockedEdit) {
        if (await rootDispatch.popup.addConfirmRemoveElement()) return true;
        else return false;
      }
      if (isLockedEditResponse.isLockedEdit && isLockedEditResponse.userHasPermission) {
        if (
          await rootDispatch.popup.addConfirmRemoveElementWithCustomMessage(
            i18n.t("editThisItemIsLockedButYouHavePermissionToChange") +
              " " +
              filterCapitalize(i18n.t("doYouReallyWantToRemoveElement?"))
          )
        )
          return true;
        else return false;
      }
      rootDispatch.popup.addAlert({
        title: filterCapitalize(i18n.t("error")),
        content: filterCapitalize(i18n.t("noPermissions")),
        type: MESSAGE_TYPES.Error,
      } as AlertModelInterface);
      return false;
    },
    async downloadDocumentPayments(context) {
      const { commit, state } = _moduleActionContext(context);
      if (state.documentId) {
        commit.setIsLoading(true);
        const response = await getDocumentPaymentsForBankStatementItem(state.documentId);
        commit.setPayments(response);
        commit.setIsLoading(false);
      }
    },
    async savePairing(context) {
      const { rootState, rootDispatch, commit, state, dispatch, rootCommit } = _moduleActionContext(context);
      if (state.bankStatementMatchDocument !== null) {
        const bankStatementItemId = rootState.finances.bankOperations.saleLeftPanel.selectedItemId as number;
        if (state.pairingType === PAIRING_TYPE.AUTOMATIC) {
          await addAutomaticallyMatchDocument(bankStatementItemId, state.bankStatementMatchDocument.documentId);
        } else {
          await addManualMatchDocument(
            bankStatementItemId,
            state.bankStatementMatchDocument.documentId,
            state.bankStatementMatchDocument.paymentAmountInCurrency
          );
        }
        rootDispatch.popup.addOperationSuccessfulSnackbar();
        await dispatch.saleLeftPanel.getDataTableData();
        rootCommit.finances.bankOperations.saleLeftPanel.setSelectedItemId(bankStatementItemId);
        // noinspection ES6MissingAwait
        dispatch.saleRightPanel.getDataTableData(bankStatementItemId);
        commit.setPairingType(null);
      }
    },
    async openManualPairingReservation(context, bookingItemId: number) {
      const { rootState, commit } = _moduleActionContext(context);
      commit.setBankStatementMatchReservation(null);
      const bankStatementItemId = rootState.finances.bankOperations.purchaseLeftPanel.selectedItemId as number;
      const response = await getBankStatementItemMatchDataReservation(bankStatementItemId, bookingItemId);
      commit.setBankStatementMatchReservation(response);
      commit.setPairingType(PAIRING_TYPE.MANUAL);
    },
    async updateManualPairingReservation(context, amount: number) {
      const { rootState, commit, state } = _moduleActionContext(context);
      const bankStatementItemId = rootState.finances.bankOperations.purchaseLeftPanel.selectedItemId as number;
      if (state.bankStatementMatchReservation !== null) {
        const response = await getManualBankStatementItemMatchDataReservation(
          bankStatementItemId,
          state.bankStatementMatchReservation.bookingItemId,
          amount
        );
        commit.setBankStatementMatchReservation(response);
      }
    },
    async openAutomaticPairingReservation(context, bookingItemId: number) {
      const { rootState, commit } = _moduleActionContext(context);
      commit.setBankStatementMatchReservation(null);
      const bankStatementItemId = rootState.finances.bankOperations.purchaseLeftPanel.selectedItemId as number;
      const response = await getBankStatementItemMatchDataReservation(bankStatementItemId, bookingItemId);
      commit.setBankStatementMatchReservation(response);
      commit.setPairingType(PAIRING_TYPE.AUTOMATIC);
    },
    async removePairingReservation(context, bookingItemId: number) {
      const { rootDispatch, rootState, dispatch, commit } = _moduleActionContext(context);
      const bankStatementItemId = rootState.finances.bankOperations.purchaseLeftPanel.selectedItemId as number;
      const confirm = await rootDispatch.popup.addConfirm(
        new ConfirmModel(
          filterCapitalize(i18n.t("doYouReallyWantToRemovePairingWithAssignedPayment?")),
          filterCapitalize(i18n.t("warning")),
          [
            {
              text: filterCapitalize(i18n.t("cancel")),
              color: "warning",
              value: false,
              outlined: true,
            },
            {
              text: filterCapitalize(i18n.t("confirm")),
              color: "warning",
              value: true,
            },
          ],
          "450px"
        )
      );
      if (confirm) {
        const isDeleted = await removeBankStatementItemMatchReservation(bankStatementItemId, bookingItemId);
        if (isDeleted) {
          rootDispatch.popup.addOperationSuccessfulSnackbar();
          await dispatch.purchaseLeftPanel.getDataTableData();
          commit.purchaseLeftPanel.setSelectedItemId(bankStatementItemId);
          // noinspection ES6MissingAwait
          dispatch.purchaseRightPanel.getDataTableData(bankStatementItemId);
        } else {
          const openDialog = await rootDispatch.popup.addConfirm(
            new ConfirmModel(
              filterCapitalize(i18n.t("xTurBankStatementToManyMatchesException")),
              filterCapitalize(i18n.t("warning")),
              [
                {
                  text: filterCapitalize(i18n.t("yes")),
                  color: "warning",
                  value: true,
                  outlined: true,
                },
                {
                  text: filterCapitalize(i18n.t("no")),
                  color: "warning",
                  value: false,
                },
              ],
              "500px"
            )
          );
          if (openDialog) {
            commit.setBookingItemId(bookingItemId);
            dispatch.downloadReservationPayments();
            commit.setPaymentDialog(true);
          }
        }
      }
    },
    async downloadReservationPayments(context) {
      const { commit, state } = _moduleActionContext(context);
      if (state.bookingItemId) {
        commit.setIsLoading(true);
        const response = await getReservationPaymentsForBankStatementItem(state.bookingItemId);
        commit.setPayments(response);
        commit.setIsLoading(false);
      }
    },
    async savePairingReservation(context) {
      const { rootState, rootDispatch, commit, state, dispatch, rootCommit } = _moduleActionContext(context);
      if (state.bankStatementMatchReservation !== null) {
        const bankStatementItemId = rootState.finances.bankOperations.purchaseLeftPanel.selectedItemId as number;
        if (state.pairingType === PAIRING_TYPE.AUTOMATIC) {
          await addAutomaticallyMatchReservation(
            bankStatementItemId,
            state.bankStatementMatchReservation.bookingItemId
          );
        } else {
          await addManualMatchReservation(
            bankStatementItemId,
            state.bankStatementMatchReservation.bookingItemId,
            state.bankStatementMatchReservation.paymentAmountInCurrency
          );
        }
        rootDispatch.popup.addOperationSuccessfulSnackbar();
        await dispatch.purchaseLeftPanel.getDataTableData();
        rootCommit.finances.bankOperations.purchaseLeftPanel.setSelectedItemId(bankStatementItemId);
        // noinspection ES6MissingAwait
        dispatch.purchaseRightPanel.getDataTableData(bankStatementItemId);
        commit.setPairingType(null);
      }
    },
  },
});

export default BankOperationsModule;

/* eslint-disable */
// noinspection JSUnusedLocalSymbols
const _moduleGetterContext = (args: [any, any, any, any]) =>
  moduleGetterContext(args, BankOperationsModule);
const _moduleActionContext = (context: any) =>
  moduleActionContext(context, BankOperationsModule);
